import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import { tenantApi } from '../../../../api/endpoints/tenant';
import { handleApiError } from '../../../../api/errorMapping';
import { Gate } from '../../../../components/permissions/Gate';
import { ConfirmDialog } from '../../../../components/shared/ConfirmDialog';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '../../../../components/ui/dropdown-menu';
import { Button } from '../../../../components/ui/button';
import { UserFieldForm } from './UserFieldForm';
import type { UserFieldDefLocal } from '../../../../api/types/TenantLocal';

interface Props {
    field: UserFieldDefLocal;
    /** tenantId del DRILL-DOWN (param de ruta), NO el del user logueado. */
    tenantId: string;
}

/**
 * Menú "⋮" de acciones por fila del catálogo de campos personalizados (drill-down PLATFORM).
 * Todas las acciones van contra `tenantApi.*UserField` sobre el `tenantId` del path y están gateadas
 * por `platform.tenant.userfields.manage` (scope PLATFORM); el backend re-enforza.
 *
 * DELETE vs DISABLE (decisión UX):
 *  - "Habilitar/Deshabilitar" (toggle vía update `enabled`) oculta el campo de los forms de usuario
 *    SIN perder el histórico de valores. Es la acción reversible y por defecto recomendada.
 *  - "Eliminar" (DELETE) quita el campo del catálogo de forma permanente. Pide confirmación
 *    destructiva. Se ofrece para limpiar campos creados por error.
 */
export function UserFieldRowActions({ field, tenantId }: Props) {
    const qc = useQueryClient();
    const [editOpen, setEditOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const invalidate = (): Promise<void> =>
        qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'user-fields'] });

    const deleteMut = useMutation({
        mutationFn: () => tenantApi.deleteUserField(tenantId, field.key),
        onSuccess: async () => {
            toast.success('Campo eliminado');
            setConfirmDelete(false);
            await invalidate();
        },
        onError: handleApiError,
    });

    // Toggle de habilitado: reusa el update parcial (solo `enabled`). key/type/label se conservan.
    const toggleMut = useMutation({
        mutationFn: () =>
            tenantApi.updateUserField(tenantId, field.key, { enabled: !field.enabled }),
        onSuccess: async () => {
            toast.success(field.enabled ? 'Campo deshabilitado' : 'Campo habilitado');
            await invalidate();
        },
        onError: handleApiError,
    });

    return (
        <Gate permission="platform.tenant.userfields.manage" scope="PLATFORM">
            {/* modal={false}: evita el page-lock de Radix (pointer-events:none en body) cuando la
                fila se desmonta tras confirmar una acción. */}
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" aria-label="Acciones">
                        <MoreVertical className="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem
                        onClick={() => {
                            setEditOpen(true);
                        }}
                    >
                        Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        disabled={toggleMut.isPending}
                        onClick={() => {
                            toggleMut.mutate();
                        }}
                    >
                        {field.enabled ? 'Deshabilitar' : 'Habilitar'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={() => {
                            setConfirmDelete(true);
                        }}
                    >
                        Eliminar
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <ConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title="Eliminar campo"
                description={`El campo "${field.label}" (${field.key}) se eliminará del catálogo de forma permanente. Si solo quieres ocultarlo de los formularios sin perder el histórico, usa "Deshabilitar". ¿Continuar?`}
                confirmLabel="Eliminar"
                destructive
                loading={deleteMut.isPending}
                onConfirm={() => {
                    deleteMut.mutate();
                }}
            />

            {/* Editar: modal plano (mismo patrón que el resto del drill-down). */}
            {editOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setEditOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Editar campo</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setEditOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <UserFieldForm
                            tenantId={tenantId}
                            field={field}
                            onDone={() => {
                                setEditOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}
        </Gate>
    );
}
